/**
 * Pancakes PDP — web component
 * Loaded deferred to avoid blocking the main thread.
 * Depends on window.NIP_DATA (flipper-nip-data.js, also deferred but listed first).
 */
if (!customElements.get('pancakes-pdp')) {
  class PancakesPDP extends HTMLElement {
    connectedCallback() {
      this.data = JSON.parse(this.querySelector('[data-pdp-data]').textContent);
      this.variantInput = this.querySelector('[data-pdp-variant-input]');
      this.sellingPlanInput = this.querySelector('[data-pdp-selling-plan-input]');
      this.quantityInput = this.querySelector('[data-pdp-quantity-input]');
      this.atcPrice = this.querySelector('[data-pdp-atc-price]');
      this.atcBtn = this.querySelector('.pdp-atc-btn');
      this.currentVariant = this.data.variants.find(v => v.id === parseInt(this.variantInput.value));
      this.currentSellingPlanId = null;

      this.bindSizeToggle();
      this.bindFlavourSwitch();
      this.bindFrequencyToggle();
      this.bindQuantitySelect();
      this.bindDropdownClose();
      this.bindJumpLinks();
      this.bindNIPModal();
      this.populateNIPAccordion();
      this.updateStepInfo();
    }

    bindSizeToggle() {
      this.querySelectorAll('[data-pdp-size-radio]').forEach(radio => {
        radio.addEventListener('change', () => {
          const variantId = parseInt(radio.value);
          this.variantInput.value = variantId;
          this.currentVariant = this.data.variants.find(v => v.id === variantId);
          this.updatePrices();
          this.updateStepInfo();
          this.updateAvailability();

          this.dispatchEvent(new CustomEvent('variant:change', {
            bubbles: true,
            detail: { variant: this.currentVariant }
          }));
        });
      });
    }

    bindFlavourSwitch() {
      this.querySelectorAll('[data-flavour-handle]').forEach(btn => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const handle = btn.dataset.flavourHandle;
          if (handle && handle !== this.data.productHandle) {
            this.switchFlavour(handle);
          }
          const details = btn.closest('details');
          if (details) details.removeAttribute('open');
        });
      });
    }

    async switchFlavour(handle) {
      this.classList.add('pdp-loading');
      const sectionId = this.dataset.sectionId;

      try {
        const response = await fetch(`/products/${handle}?sections=${sectionId}`);
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        const html = json[sectionId];

        const temp = document.createElement('div');
        temp.innerHTML = html;
        const newPdp = temp.querySelector('pancakes-pdp');

        if (newPdp) {
          this.replaceWith(newPdp);

          history.replaceState({}, '', `/products/${handle}`);

          /* Refresh Junip star summaries + full review section for the new product */
          try {
            const newData = JSON.parse(newPdp.querySelector('[data-pdp-data]').textContent);
            const newProductId = String(newData.productId);

            /* Star summaries – replace with fresh elements so Junip re-initialises */
            document.querySelectorAll('.junip-product-summary').forEach(el => {
              const fresh = document.createElement('span');
              fresh.className = 'junip-product-summary';
              fresh.setAttribute('data-product-id', newProductId);
              el.replaceWith(fresh);
            });

            /* Full review widget – replace with a brand-new element so Junip
               treats it as an uninitialised widget and fetches new reviews */
            document.querySelectorAll('.junip-product-review').forEach(el => {
              const fresh = document.createElement('span');
              fresh.className = 'junip-product-review';
              fresh.setAttribute('data-product-id', newProductId);
              el.replaceWith(fresh);
            });

            /* Re-initialise Junip so it discovers the new elements */
            if (window.Junip) {
              if (typeof window.Junip.init === 'function') window.Junip.init();
              if (typeof window.Junip.render === 'function') window.Junip.render();
            }
          } catch (junipError) {
            /* Junip refresh is best-effort */
          }
        } else {
          throw new Error('Could not parse section HTML');
        }
      } catch (error) {
        console.warn('Flavour switch failed, falling back to navigation:', error);
        window.location.href = `/products/${handle}`;
      }
    }

    bindFrequencyToggle() {
      const subRadio = this.querySelector('[data-pdp-frequency-radio][value="subscription"]');
      const freqSelect = this.querySelector('[data-pdp-frequency-select]');

      this.querySelectorAll('[data-pdp-frequency-radio]').forEach(radio => {
        radio.addEventListener('change', () => {
          if (radio.value === 'subscription' && subRadio) {
            const planId = freqSelect ? freqSelect.value : subRadio.dataset.sellingPlanId;
            this.sellingPlanInput.value = planId;
            this.sellingPlanInput.disabled = false;
            this.currentSellingPlanId = parseInt(planId);
          } else {
            this.sellingPlanInput.value = '';
            this.sellingPlanInput.disabled = true;
            this.currentSellingPlanId = null;
          }
          this.updatePrices();
        });
      });

      if (freqSelect) {
        freqSelect.addEventListener('change', () => {
          if (subRadio && subRadio.checked) {
            const planId = freqSelect.value;
            this.sellingPlanInput.value = planId;
            this.currentSellingPlanId = parseInt(planId);
            subRadio.dataset.sellingPlanId = planId;
            this.updatePrices();
          }
        });
      }
    }

    bindQuantitySelect() {
      this.querySelectorAll('[data-pdp-quantity-option]').forEach(option => {
        option.addEventListener('click', (e) => {
          e.preventDefault();
          const qty = parseInt(option.dataset.quantity);
          this.quantityInput.value = qty;

          const display = this.querySelector('[data-pdp-quantity-display]');
          if (display) {
            display.innerHTML = option.innerHTML;
          }

          const details = option.closest('details');
          if (details) details.removeAttribute('open');

          this.updatePrices();
          this.updateStepInfo();
        });
      });
    }

    bindJumpLinks() {
      this.querySelectorAll('.pdp-learn-more__link').forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          let key = link.getAttribute('href')?.replace('#', '');
          if (!key) return;
          key = key.replace(/^shopify-section-/, '');
          /* Find the section wrapper: Shopify renders JSON-template sections as
             <div id="shopify-section-template--XXXX--{key}"> so we search all
             .shopify-section elements for one whose ID contains our key. */
          let target = null;
          document.querySelectorAll('.shopify-section').forEach(el => {
            if (el.id && el.id.indexOf(key) !== -1) target = el;
          });
          if (!target) target = document.getElementById(key);
          if (target) {
            /* scroll-padding-block-start on <html> already accounts for the
               sticky header, so scrollIntoView lands in the right spot. */
            target.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        });
      });
    }

    bindDropdownClose() {
      document.addEventListener('click', (e) => {
        this.querySelectorAll('details.pdp-flavour-select, details.pdp-quantity-select').forEach(details => {
          if (!details.contains(e.target)) {
            details.removeAttribute('open');
          }
        });
      });
    }

    getQtyDiscount(qty) {
      const discounts = this.data.quantityDiscounts || {};
      return (discounts[String(qty)] || 0) / 100;
    }

    updatePrices() {
      if (!this.currentVariant) return;

      const qty = parseInt(this.quantityInput?.value) || 1;
      const qtyDiscount = this.getQtyDiscount(qty);

      /* One-Time unit price (with qty discount applied) */
      const otpUnit = Math.round(this.currentVariant.price * (1 - qtyDiscount));

      /* Subscription unit price */
      let subUnit = null;
      let subCompareUnit = null;
      if (this.currentSellingPlanId) {
        const alloc = this.currentVariant.sellingPlanAllocations.find(
          a => a.sellingPlanId === this.currentSellingPlanId
        );
        if (alloc) {
          subUnit = Math.round(alloc.price * (1 - qtyDiscount));
          subCompareUnit = Math.round(alloc.compareAtPrice * (1 - qtyDiscount));
        }
      }

      /* ATC button total */
      const activeUnit = subUnit != null ? subUnit : otpUnit;
      if (this.atcPrice) {
        this.atcPrice.textContent = this.formatMoney(activeUnit * qty);
      }

      /* OTP radio price */
      const onetimePrice = this.querySelector('[data-pdp-onetime-price]');
      if (onetimePrice) {
        onetimePrice.textContent = this.formatMoney(otpUnit * qty);
      }

      this.updateSubscriptionPrices(qty, qtyDiscount);
      this.updateSizeButtonPrices();
    }

    updateSubscriptionPrices(qty, qtyDiscount) {
      if (!this.currentVariant) return;
      if (qty == null) qty = parseInt(this.quantityInput?.value) || 1;
      if (qtyDiscount == null) qtyDiscount = this.getQtyDiscount(qty);

      const activePlanId = this.currentSellingPlanId
        || parseInt(this.querySelector('[data-pdp-frequency-select]')?.value)
        || parseInt(this.querySelector('[data-pdp-frequency-radio][value="subscription"]')?.dataset.sellingPlanId);

      const alloc = this.currentVariant.sellingPlanAllocations.find(a => a.sellingPlanId === activePlanId)
        || this.currentVariant.sellingPlanAllocations[0];

      if (!alloc) return;

      const subUnit = Math.round(alloc.price * (1 - qtyDiscount));
      const compareUnit = Math.round(alloc.compareAtPrice * (1 - qtyDiscount));

      const priceEl = this.querySelector('[data-pdp-sub-price]');
      const compareEl = this.querySelector('[data-pdp-sub-compare]');
      if (priceEl) priceEl.textContent = this.formatMoney(subUnit * qty);
      if (compareEl) compareEl.textContent = this.formatMoney(compareUnit * qty);

      const badgeEl = this.querySelector('[data-pdp-save-badge]');
      if (badgeEl && this.currentVariant.price > 0) {
        const pct = Math.round((1 - alloc.price / this.currentVariant.price) * 100);
        badgeEl.textContent = `save ${pct}%`;
      }
    }

    updateSizeButtonPrices() {
      this.querySelectorAll('[data-pdp-size-radio]').forEach(radio => {
        const variant = this.data.variants.find(v => v.id === parseInt(radio.value));
        const priceEl = radio.closest('.pdp-size-option').querySelector('[data-pdp-size-price]');
        if (priceEl && variant) {
          priceEl.textContent = this.formatMoney(variant.price);
        }
      });
    }

    updateStepInfo() {
      if (!this.currentVariant) return;

      const subtitle = this.querySelector('[data-pdp-size-subtitle]');
      if (subtitle) {
        const option = this.currentVariant.option1;
        const grams = this.parseGrams(option);
        const servings = grams ? Math.round(grams / this.data.gramsPerServe) : null;
        subtitle.innerHTML = servings
          ? `<strong>${option}</strong> | ${servings} serves / bag`
          : `<strong>${option}</strong>`;
      }

      this.querySelectorAll('[data-pdp-quantity-option]').forEach(opt => {
        const qty = parseInt(opt.dataset.quantity);
        const servingsEl = opt.querySelector('[data-pdp-qty-servings]');
        if (servingsEl) {
          const grams = this.parseGrams(this.currentVariant.option1);
          const perBag = grams ? Math.round(grams / this.data.gramsPerServe) : null;
          servingsEl.textContent = perBag ? `${qty * perBag} servings` : '';
        }
      });

      const qtyDisplay = this.querySelector('[data-pdp-quantity-display] [data-pdp-qty-servings]');
      if (qtyDisplay) {
        const qty = parseInt(this.quantityInput.value) || 1;
        const grams = this.parseGrams(this.currentVariant.option1);
        const perBag = grams ? Math.round(grams / this.data.gramsPerServe) : null;
        qtyDisplay.textContent = perBag ? `${qty * perBag} servings` : '';
      }
    }

    updateAvailability() {
      if (!this.currentVariant) return;
      if (this.atcBtn) {
        const isAvailable = this.currentVariant.available;
        this.atcBtn.disabled = !isAvailable;
        if (isAvailable) {
          const qty = parseInt(this.quantityInput?.value) || 1;
          const price = this.formatMoney(this.currentVariant.price * qty);
          this.atcBtn.innerHTML = `<span>ADD TO CART</span><span aria-hidden="true">&bull;</span><span data-pdp-atc-price>${price}</span>`;
          this.atcPrice = this.querySelector('[data-pdp-atc-price]');
        } else {
          this.atcBtn.innerHTML = `<span>SOLD OUT</span>`;
          this.atcPrice = null;
        }
      }
    }

    parseGrams(optionValue) {
      if (!optionValue) return 0;
      const lower = optionValue.toLowerCase().trim();
      const kgMatch = lower.match(/([\d.]+)\s*kg/);
      if (kgMatch) return parseFloat(kgMatch[1]) * 1000;
      const gMatch = lower.match(/([\d.]+)\s*g/);
      if (gMatch) return parseFloat(gMatch[1]);
      return 0;
    }

    formatMoney(cents) {
      if (!this._moneyFmt) {
        try {
          const lang = document.documentElement.lang || 'en-AU';
          this._moneyFmt = new Intl.NumberFormat(lang, {
            style: 'currency',
            currency: this.data.currency || 'AUD'
          });
        } catch (e) {
          this._moneyFmt = null;
        }
      }
      if (this._moneyFmt) return this._moneyFmt.format(cents / 100);
      return '$' + (cents / 100).toFixed(2);
    }

    /* ---- NIP (Nutritional Info Panel) Modal ---- */

    getNIPForProduct(title) {
      if (!title || !window.NIP_DATA) return null;
      /* Exact match first */
      if (window.NIP_DATA[title]) return window.NIP_DATA[title];
      /* Case-insensitive / partial match */
      const lower = title.toLowerCase();
      for (const [key, val] of Object.entries(window.NIP_DATA)) {
        if (lower === key.toLowerCase() || lower.includes(key.toLowerCase())) return val;
      }
      return null;
    }

    bindNIPModal() {
      const trigger = this.querySelector('[data-pdp-nip-trigger]');
      const overlay = this.querySelector('[data-pdp-nip-overlay]');
      if (!trigger || !overlay) return;

      const modal = overlay.querySelector('.pdp-nip-modal');
      const closeBtn = overlay.querySelector('[data-pdp-nip-close]');

      trigger.addEventListener('click', () => {
        this.populateNIP();
        overlay.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      });

      const closeModal = () => {
        modal.style.transform = '';
        modal.classList.remove('is-dragging');
        overlay.style.opacity = '';
        overlay.classList.remove('is-open');
        document.body.style.overflow = '';
      };

      closeBtn?.addEventListener('click', closeModal);
      overlay.addEventListener('click', (e) => {
        if (e.target === overlay) closeModal();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeModal();
      });

      /* Swipe-to-dismiss (mobile bottom-sheet) */
      let startY = 0;
      let currentY = 0;
      let isDragging = false;

      modal.addEventListener('touchstart', (e) => {
        /* Only allow drag when scrolled to top, or touching the drag handle area (top 40px) */
        const touchY = e.touches[0].clientY;
        const modalRect = modal.getBoundingClientRect();
        const touchOffset = touchY - modalRect.top;
        if (modal.scrollTop > 0 && touchOffset > 40) return;
        startY = touchY;
        isDragging = true;
        modal.classList.add('is-dragging');
      }, { passive: true });

      modal.addEventListener('touchmove', (e) => {
        if (!isDragging) return;
        currentY = e.touches[0].clientY;
        const delta = currentY - startY;
        if (delta > 0) {
          modal.style.transform = `translateY(${delta}px)`;
          overlay.style.opacity = Math.max(0.2, 1 - delta / 400);
        }
      }, { passive: true });

      modal.addEventListener('touchend', () => {
        if (!isDragging) return;
        isDragging = false;
        modal.classList.remove('is-dragging');
        const delta = currentY - startY;
        if (delta > 100) {
          closeModal();
        } else {
          modal.style.transform = '';
          overlay.style.opacity = '';
        }
        startY = 0;
        currentY = 0;
      });
    }

    populateNIP() {
      const title = this.data.productTitle || '';
      const nip = this.getNIPForProduct(title);
      if (!nip) return;

      const flavourEl = this.querySelector('[data-nip-flavour-name]');
      const ingredientsEl = this.querySelector('[data-nip-ingredients]');
      const allergensEl = this.querySelector('[data-nip-allergens]');
      const tbody = this.querySelector('[data-nip-tbody]');
      if (!tbody) return;

      if (flavourEl) flavourEl.textContent = title;
      if (ingredientsEl) ingredientsEl.textContent = nip.ingredients;
      if (allergensEl) {
        allergensEl.textContent = nip.allergens;
        allergensEl.style.display = nip.allergens ? '' : 'none';
      }

      /* Build table rows */
      let html = '';
      html += `<tr><th>Servings per package:</th><td>${nip.servings}</td></tr>`;
      html += `<tr><th>Serving size</th><td>${nip.servingSize}</td></tr>`;
      html += `<tr class="pdp-nip-table__calories"><th>Calories</th><td>${nip.calories}</td></tr>`;
      for (const row of nip.rows) {
        const isSubRow = row[0].startsWith('_');
        const label = isSubRow ? row[0].slice(1) : row[0];
        const cls = isSubRow ? ' class="pdp-nip-table__subrow"' : '';
        html += `<tr${cls}><th>${label} ${row[1]}</th><td>${row[2]}</td></tr>`;
      }

      tbody.innerHTML = html;
    }

    populateNIPAccordion() {
      const accWrap = this.querySelector('[data-pdp-nip-accordion]');
      if (!accWrap) return;

      const title = this.data.productTitle || '';
      const nip = this.getNIPForProduct(title);
      if (!nip) return;

      const ingredientsEl = accWrap.querySelector('[data-nip-acc-ingredients]');
      const allergensEl = accWrap.querySelector('[data-nip-acc-allergens]');
      const tbody = accWrap.querySelector('[data-nip-acc-tbody]');

      if (ingredientsEl) ingredientsEl.textContent = nip.ingredients;
      if (allergensEl) {
        allergensEl.textContent = nip.allergens;
        allergensEl.style.display = nip.allergens ? '' : 'none';
      }
      if (!tbody) return;

      let html = '';
      html += `<tr><th>Servings per package:</th><td>${nip.servings}</td></tr>`;
      html += `<tr><th>Serving size</th><td>${nip.servingSize}</td></tr>`;
      html += `<tr class="pdp-nip-table__calories"><th>Calories</th><td>${nip.calories}</td></tr>`;
      for (const row of nip.rows) {
        const isSubRow = row[0].startsWith('_');
        const label = isSubRow ? row[0].slice(1) : row[0];
        const cls = isSubRow ? ' class="pdp-nip-table__subrow"' : '';
        html += `<tr${cls}><th>${label} ${row[1]}</th><td>${row[2]}</td></tr>`;
      }
      tbody.innerHTML = html;
    }
  }

  customElements.define('pancakes-pdp', PancakesPDP);
}
